"use client";

import type { ChatStatus, ToolUIPart as ToolPartType } from "ai";
import type { ReactElement } from "react";
import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
} from "react";
import type { AgentUIMessage } from "@/lib/ai/types";

/**
 * Tool part plugin が描画時に受け取る共通引数。
 *
 * `messageState` は plugin ごとに事前計算したメッセージ単位 state を受け渡す。
 */
export type ToolPartPluginRenderArgs = {
  part: ToolPartType;
  partIndex: number;
  message: AgentUIMessage;
  chatStatus: ChatStatus;
  isLastMessage: boolean;
  messageState: unknown;
};

/**
 * ChatMessages が tool 表示責務を委譲する最小 plugin 契約。
 *
 * - `getMessageState`: メッセージ単位の前処理
 * - `shouldHidePart`: plugin が担当する重複 part の描画抑止
 * - `tryRender`: plugin が担当する tool part だけを描画
 *
 * `tryRender` は対象外 part のとき `null` を返し、host 側に tool 名判定を
 * 持ち込まない。これによりドメイン固有の描画責務を plugin 側へ閉じ込める。
 */
export type ChatToolPartPlugin = {
  /**
   * tool 呼び出しを Activity に残しながら、plugin の rich output を通常回答側へ
   * 分離する場合に指定する。省略時は従来どおり plugin 全体を standalone 描画する。
   */
  placement?: "standalone" | "activity-and-standalone";
  /** Activity に表示する tool 呼び出し名。省略時は tool type から生成する。 */
  toolCallTitle?: string;
  getMessageState?: (message: AgentUIMessage) => unknown;
  /** rich output だけでなく Activity 行も含め、対象 part を描画モデルから除外する。 */
  shouldHidePart?: (args: ToolPartPluginRenderArgs) => boolean;
  tryRender: (args: ToolPartPluginRenderArgs) => ReactElement | null;
};

export type ChatClientDataPart = {
  type: string;
  data?: unknown;
};

export type ChatClientRequestBody = Record<string, unknown>;

/**
 * AI chat client に機能を差し込む最小 plugin 契約。
 *
 * @remarks
 * request body・stream data・error policy・tool-part 描画の 4 つだけを拡張点として
 * 固定し、thread 層へ機能固有 props を増やさない。
 */
export type ChatClientPlugin = {
  id: string;
  extendRequestBody?: (body: ChatClientRequestBody) => void;
  handleDataPart?: (part: ChatClientDataPart) => boolean;
  shouldSuppressError?: (error: Error) => boolean;
  onThreadScopeChange?: () => void;
  getToolPartPlugins?: (messages: AgentUIMessage[]) => ChatToolPartPlugin[];
};

const ChatClientPluginContext = createContext<ChatClientPlugin[]>([]);

/**
 * AI chat client plugin 群を thread 配下へ共有する。
 *
 * @remarks
 * plugin の登録順や有効化条件は機能ごとの composition root 側で決め、
 * consumer 側では generic な `useChatClientPlugins()` だけを使う。
 */
export function ChatClientPluginProvider({
  children,
  plugins,
}: {
  children: ReactNode;
  plugins: ChatClientPlugin[];
}) {
  return (
    <ChatClientPluginContext.Provider value={plugins}>
      {children}
    </ChatClientPluginContext.Provider>
  );
}

/**
 * 現在の AI chat client plugin 一覧を返す。
 *
 * provider 不在時は空配列を返し、plugin 非導入状態でも thread 本体を動作させる。
 */
export function useChatClientPlugins(): ChatClientPlugin[] {
  return useContext(ChatClientPluginContext);
}

/**
 * 現在有効な client plugin から tool-part plugin を収集する。
 *
 * `messages` は描画時点の履歴をそのまま渡し、各 plugin が必要な前処理を
 * 自分のドメイン内で完結できるようにする。
 */
export function useChatToolPartPlugins(
  messages: AgentUIMessage[],
): ChatToolPartPlugin[] {
  const plugins = useChatClientPlugins();
  const cachedPluginsRef = useRef<ChatToolPartPlugin[]>([]);

  return useMemo(() => {
    const nextPlugins = plugins.flatMap(
      (plugin) => plugin.getToolPartPlugins?.(messages) ?? [],
    );
    const cachedPlugins = cachedPluginsRef.current;
    const canReuseCachedPlugins =
      cachedPlugins.length === nextPlugins.length &&
      cachedPlugins.every(
        (cachedPlugin, index) => cachedPlugin === nextPlugins[index],
      );

    if (canReuseCachedPlugins) {
      return cachedPlugins;
    }

    // message 配列は streaming 中に毎 tick 置き換わるため、plugin 自体が不変なら
    // ここでも同じ配列参照を返して既存 chart 行の不要な再描画を止める。
    cachedPluginsRef.current = nextPlugins;
    return nextPlugins;
  }, [messages, plugins]);
}
